import { t } from "@lingui/macro";
import { I18n } from "@lingui/core";
import {
  CONTEXT_LIST,
  RESPONSIBLE_LIST,
  WW_RESPONSIBLE_LIST,
  PRIORITY_LIST,
  TRANSITION_REASON_LIST,
  STATUS_LIST,
} from "../../../utils/lists";

export function getContextMap(i18n: I18n): { [key: string]: string } {
  return {
    Maintenance: i18n._(t("context_list.maintenance_label")`Maintenance`),
    DLP: i18n._(t("context_list.dlp_label")`DLP`),
    Optimisation: i18n._(t("context_list.optimisation_label")`Optimisation`),
    Other: i18n._(t("context_list.other_label")`Other`),
  };
}

export function getContextOptions(i18n: I18n) {
  const labelMap: { [key: string]: string } = getContextMap(i18n);
  return CONTEXT_LIST.map((l) => ({ value: l, label: labelMap[l] }));
}

export function getResponsibleMap(
  i18n: I18n,
  isWOOL = false
): { [key: string]: string } {
  return isWOOL
    ? {
        "Asset Team - Building": i18n._(
          t("responsible_list.asset_team_building")`Asset Team - Building`
        ),
        "Asset Team - Electrical": i18n._(
          t("responsible_list.asset_team_electrical")`Asset Team - Electrical`
        ),
        "Asset Team - Plumbing": i18n._(
          t("responsible_list.asset_team_plumbing")`Asset Team - Plumbing`
        ),
        "Asset Team - Refrigeration": i18n._(
          t(
            "responsible_list.asset_team_refrigeration"
          )`Asset Team - Refrigeration`
        ),
        "EMC - Energy Optimisation Specialist": i18n._(
          t(
            "responsible_list.emc_energy_optimisation_specialist"
          )`EMC - Energy Optimisation Specialist`
        ),
        "EMC - Operations/IT": i18n._(
          t("responsible_list.emc_operations_it")`EMC - Operations/IT`
        ),
        "External Contractors (Back to Back)": i18n._(
          t(
            "responsible_list.external_contractors_back_to_back"
          )`External Contractors (Back to Back)`
        ),
        "External Contractors (Others)": i18n._(
          t(
            "responsible_list.external_contractors_others"
          )`External Contractors (Others)`
        ),
        "Field Maintenance Technician (FMT) - HVAC": i18n._(
          t(
            "responsible_list.fmt_hvac"
          )`Field Maintenance Technician (FMT) - HVAC`
        ),
        "Field Maintenance Technician (FMT) - Refrigeration": i18n._(
          t(
            "responsible_list.fmt_refrigeration"
          )`Field Maintenance Technician (FMT) - Refrigeration`
        ),
        "Service Team (STL)": i18n._(
          t("responsible_list.service_team")`Service Team (STL)`
        ),
        "State Admin Manager (SAM)": i18n._(
          t("responsible_list.state_admin_manager")`State Admin Manager (SAM)`
        ),
        "Technical Support Team (TST)": i18n._(
          t(
            "responsible_list.technical_support_team"
          )`Technical Support Team (TST)`
        ),
        "WFM Support Centre": i18n._(
          t("responsible_list.wfm_support_centre")`WFM Support Centre`
        ),
        Other: i18n._(t("responsible_list.other")`Other`),
        ConserveIt: i18n._(t("responsible_list.conserveit")`ConserveIt`),
      }
    : {
        ConserveIt: i18n._(t("responsible_list.conserveit")`ConserveIt`),
        "Site Local": i18n._(t("responsible_list.site_local")`Site Local`),
        "Site National": i18n._(
          t("responsible_list.site_national")`Site National`
        ),
        "Mech. Contractor": i18n._(
          t("responsible_list.mech_contractor")`Mech. Contractor`
        ),
        "BMS Contractor": i18n._(
          t("responsible_list.bms_contractor")`BMS Contractor`
        ),
        "Other Contractor": i18n._(
          t("responsible_list.other_contractor")`Other Contractor`
        ),
        "InSite Engineer": i18n._(
          t("responsible_list.insite_engineer")`InSite Engineer`
        ),
        Monitoring: i18n._(t("responsible_list.monitoring")`Monitoring`),
      };
}

export function getResponsibleOptions(
  userGroups: string[],
  i18n: I18n,
  legacySiteId = ""
) {
  // HACK: Mistakes were made...
  // If selected site is a WOOL site, return WOOL responsible list
  const isWoolSite = legacySiteId.split("~")[0] === "WOOL";
  const list =
    userGroups.includes("WOOL") && isWoolSite
      ? WW_RESPONSIBLE_LIST
      : RESPONSIBLE_LIST;

  const labelMap: { [key: string]: string } = getResponsibleMap(
    i18n,
    isWoolSite
  );
  return list.map((l) => ({ value: l, label: labelMap[l] }));
}

export function getPriorityMap(i18n: I18n): { [key: string]: string } {
  return {
    Blocker: i18n._(t("priority_list.blocker")`Blocker`),
    Critical: i18n._(t("priority_list.critical")`Critical`),
    Major: i18n._(t("priority_list.major")`Major`),
    Moderate: i18n._(t("priority_list.moderate")`Moderate`),
    Minor: i18n._(t("priority_list.minor")`Minor`),
    Trivial: i18n._(t("priority_list.trivial")`Trivial`),
  };
}

export function getPriorityOptions(i18n: I18n) {
  const labelMap: { [key: string]: string } = getPriorityMap(i18n);
  return PRIORITY_LIST.map((l) => ({ value: l, label: labelMap[l] }));
}

export function getTransitionReasonMap(i18n: I18n): { [key: string]: string } {
  return {
    Fixed: i18n._(t("transition_reason_list.fixed")`Fixed`),
    "False alert": i18n._(t("transition_reason_list.false_alert")`False alert`),
    "Cannot fix (known issue)": i18n._(
      t("transition_reason_list.cannot_fix")`Cannot fix (known issue)`
    ),
    Duplicate: i18n._(t("transition_reason_list.duplicate")`Duplicate`),
    "Too difficult/expensive": i18n._(
      t(
        "transition_reason_list.too_difficult_expensive"
      )`Too difficult/expensive`
    ),
    "Low priority": i18n._(
      t("transition_reason_list.low_priority")`Low priority`
    ),
    Other: i18n._(t("transition_reason_list.other")`Other`),
  };
}

export function getTransitionReasonOptions(i18n: I18n) {
  const labelMap = getTransitionReasonMap(i18n);
  return TRANSITION_REASON_LIST.map((l) => ({ value: l, label: labelMap[l] }));
}

export function getStatusMap(i18n: I18n): { [key: string]: string } {
  return {
    Open: i18n._(t("status_list.open")`Open`),
    "To Be Raised MM": i18n._(
      t("status_list.to_be_raised_mm")`To Be Raised MM`
    ),
    "Work in progress": i18n._(
      t("status_list.work_in_progress")`Work in progress`
    ),
    "Scope Required": i18n._(t("status_list.scope_required")`Scope Required`),
    "To be quoted": i18n._(t("status_list.to_be_quoted")`To be quoted`),
    "Pending PO": i18n._(t("status_list.pending_po")`Pending PO`),
    "In Review": i18n._(t("status_list.in_review")`In Review`),
    Parked: i18n._(t("status_list.parked")`Parked`),
    Closed: i18n._(t("status_list.closed")`Closed`),
    Rejected: i18n._(t("status_list.rejected")`Rejected`),
  };
}

export function getStatusOptions(i18n: I18n) {
  const labelMap = getStatusMap(i18n);
  return STATUS_LIST.map((l) => ({ value: l, label: labelMap[l] }));
}
